import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./query-keys";
import {
  fetchProduct,
  fetchUserProducts,
  fetchAllProducts,
  saveProductToFirebase,
  deleteProductFromFirebase,
  type Product,
  type SaveProductParams,
  type UserProductsQuery,
  type AdminProductsQuery,
} from "../api/products";

// Query options for single product
export const productQueryOptions = (uid: string) =>
  queryOptions({
    queryKey: queryKeys.products.detail(uid),
    queryFn: () => fetchProduct(uid),
    enabled: !!uid,
  });

// Query options for user's products list
export const userProductsQueryOptions = (query: UserProductsQuery) =>
  queryOptions({
    queryKey: queryKeys.products.userList(query.userId, query.status, query.name),
    queryFn: () => fetchUserProducts(query),
    enabled: !!query.userId,
  });

// Query options for admin products list (all users)
export const adminProductsQueryOptions = (query: AdminProductsQuery) =>
  queryOptions({
    queryKey: queryKeys.products.adminList(query.status, query.name),
    queryFn: () => fetchAllProducts(query),
  });

// Save product mutation with optimistic updates
export const useSaveProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SaveProductParams) => saveProductToFirebase(params),
    onMutate: async (variables) => {
      const { product } = variables;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.products.detail(product.uid),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<Product>(queryKeys.products.detail(product.uid));

      // Optimistically update
      queryClient.setQueryData(queryKeys.products.detail(product.uid), product);

      return { previous, product };
    },
    onError: (err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.products.detail(context.product.uid), context.previous);
      }
      console.error("Save product failed:", err);
    },
    onSettled: (_data, _error, variables) => {
      // Invalidate to refetch latest state from server
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.detail(variables.product.uid),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.userList(variables.userId),
      });
    },
  });
};

// Remove product mutation
export const useRemoveProductMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uid: string) => deleteProductFromFirebase(uid),
    onMutate: async (uid) => {
      // Cancel queries
      await queryClient.cancelQueries({
        queryKey: queryKeys.products.detail(uid),
      });

      // Snapshot
      const previous = queryClient.getQueryData<Product>(queryKeys.products.detail(uid));

      // Optimistically remove
      queryClient.removeQueries({
        queryKey: queryKeys.products.detail(uid),
      });

      return { previous, uid };
    },
    onError: (err, uid, context) => {
      // Restore on error
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.products.detail(uid), context.previous);
      }
      console.error("Delete product failed:", err);
    },
    onSuccess: () => {
      // Invalidate all user products lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.products.all(),
      });
    },
  });
};
